import React from 'react';
import { Switch, Typography, theme } from 'antd';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconTool } from '@tabler/icons-react';
import EmptyState from './EmptyState';
import { IMachineryAccessory } from '../interfaces/heavyDutyMachinery/IMachineryAccessory';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';

const { Text } = Typography;

interface AccessoryAssignmentPickerProps {
  accessories: IMachineryAccessory[];
  loading?: boolean;
  // Optional here even though this is always used inside a Form.Item, which
  // injects both at clone time -- same convention as OperatorAssignmentPicker.
  value?: string[];
  onChange?: (value: string[]) => void;
  disabled?: boolean;
  /** This machine's own Category -- only accessories whose compatibleCategories includes it are ever offered here, since the backend would silently drop any other selection anyway (see MachineryController's own AccessoryIds handling). */
  category?: string;
}

/**
 * Multi-toggle picker for "which of my own accessories does THIS machine
 * offer" -- staged Form.Item value (string[] of MachineryAccessory ids)
 * rather than MachineryAccessoryLinkSection's old immediate-effect
 * API-linked pattern, same "has to work during the create wizard, before a
 * real Machinery.Id exists to link against" reasoning as
 * OperatorAssignmentPicker (see that component's own doc comment). Shared
 * by both the create wizard and HeavyDutyMachineryEditForm so the two stay
 * in sync -- see AccessoryIds's own doc comment (API repo).
 */
const AccessoryAssignmentPicker: React.FC<AccessoryAssignmentPickerProps> = ({
  accessories,
  loading,
  value,
  onChange,
  disabled,
  category,
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const selected = new Set(value ?? []);

  const compatibleAccessories = accessories.filter(
    (a) => a.isActive && a.compatibleCategories.split(',').includes(category ?? '')
  );

  const toggle = (accessoryId: string, checked: boolean) => {
    if (disabled) return;
    const next = new Set(selected);
    if (checked) next.add(accessoryId);
    else next.delete(accessoryId);
    onChange?.(Array.from(next));
  };

  if (loading) return null;

  if (compatibleAccessories.length === 0) {
    return (
      <EmptyState
        icon={<IconTool size={20} />}
        title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_LINK_EMPTY_TITLE)}
        description={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_LINK_EMPTY_DESCRIPTION)}
        action={
          <Link to="/heavy-duty-machinery/accessories" target="_blank" rel="noopener noreferrer">
            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_LINK_EMPTY_CTA)}
          </Link>
        }
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {compatibleAccessories.map((accessory) => (
        <div
          key={accessory.id}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            padding: '10px 14px',
            borderRadius: 14,
            background: token.colorBgContainer,
            border: `1px solid ${selected.has(accessory.id) ? token.colorPrimary : token.colorBorderSecondary}`,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <Text strong style={{ display: 'block' }}>{accessory.name}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>+{accessory.pricePerDay.toFixed(2)}€/día</Text>
          </div>
          <Switch
            checked={selected.has(accessory.id)}
            disabled={disabled}
            onChange={(checked) => toggle(accessory.id, checked)}
          />
        </div>
      ))}
    </div>
  );
};

export default AccessoryAssignmentPicker;
